import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Edit } from '@mui/icons-material'
import { Button, Paper, Stack, Typography } from '@mui/material'

import { WindowVirtualizedList } from '../../../../../components/WindowVirtualizedList/WindowVirtualizedList'
import { Game } from '../../../../../features/game/types/Game'
import { InternalGameScreenshot } from '../../../../api/screenshot-links'
import { BulkAssignScreenshotCard } from '../BulkAssignScreenshotCard/BulkAssignScreenshotCard'
import { ScreenshotEnlargeDialog } from '../ScreenshotEnlargeDialog/ScreenshotEnlargeDialog'

type BulkAssignScreenshotGridProps = {
  screenshots: InternalGameScreenshot[]
  gamesById: Map<string, Game>
  selectedIds: string[]
  isLoading?: boolean
  noItemsLabel: string
  onSelectedIdsChange: (ids: string[]) => void
  onEdit: (screenshotIds: string[]) => void
}

export const BulkAssignScreenshotGrid: FC<BulkAssignScreenshotGridProps> = ({
  screenshots,
  gamesById,
  selectedIds,
  isLoading,
  noItemsLabel,
  onSelectedIdsChange,
  onEdit,
}) => {
  const { t } = useTranslation()
  const [enlargedUrl, setEnlargedUrl] = useState<string>()
  const shownIds = useMemo(() => screenshots.map(({ id }) => id), [screenshots])
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const selectedShownIds = useMemo(() => shownIds.filter((id) => selectedSet.has(id)), [shownIds, selectedSet])

  const toggle = (id: string) => onSelectedIdsChange(selectedSet.has(id) ? selectedIds.filter((selected) => selected !== id) : [...selectedIds, id])

  return (
    <>
      <Paper variant="outlined" sx={{ position: 'sticky', top: 0, zIndex: 2, p: 1, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="contained" startIcon={<Edit />} disabled={!selectedShownIds.length} onClick={() => onEdit(selectedShownIds)}>
            {t('internal-analysts:ie_bulk_assign_edit_selected', { count: selectedShownIds.length })}
          </Button>
          <Button disabled={!shownIds.length} onClick={() => onSelectedIdsChange(Array.from(new Set([...selectedIds, ...shownIds])))}>
            {t('internal-analysts:analysis_screenshots_select_all')}
          </Button>
          <Button disabled={!selectedIds.length} onClick={() => onSelectedIdsChange([])}>
            {t('internal-analysts:ie_bulk_assign_deselect_all')}
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {t('internal-analysts:analysis_screenshots_selected', { count: selectedShownIds.length, shown: shownIds.length })}
          </Typography>
        </Stack>
      </Paper>
      <WindowVirtualizedList
        items={screenshots}
        isLoading={isLoading}
        columns={{ xs: 1, sm: 2, md: 3, lg: 4 }}
        estimateSize={340}
        gap={16}
        getItemKey={(screenshot) => screenshot.id}
        noItemsLabel={<Typography color="text.secondary">{noItemsLabel}</Typography>}
        renderItem={(screenshot) => (
          <BulkAssignScreenshotCard
            screenshot={screenshot}
            game={gamesById.get(screenshot.gameId)}
            selected={selectedSet.has(screenshot.id)}
            onToggle={() => toggle(screenshot.id)}
            onEdit={() => onEdit([screenshot.id])}
            onEnlarge={() => setEnlargedUrl(screenshot.url)}
          />
        )}
      />
      <ScreenshotEnlargeDialog url={enlargedUrl} alt={t('internal-analysts:ie_bulk_assign_enlarged')} onClose={() => setEnlargedUrl(undefined)} />
    </>
  )
}
